import type { ReactNode } from 'react'
import { useBottomSheet } from '../../../../../../../state-management/contexts/BottomSheetContext'
import {
  LumpsumRebalanceImportantNoteStaticType,
  type LumpsumRebalanceImportantNotes
} from '../../api/rebalanceRecommendation.types'
import { PlanImportantNoteCard } from '../../shared/notes/PlanImportantNoteCard'
import { buildFactBasedImportantNotePoints } from '../../shared/notes/PlanImportantNoteDynamicInfoBottomSheet'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import {
  ElssRebalancingInfoBottomSheet,
  GrowthPlanInsteadOfIdcwInfoBottomSheet,
  RankOneNotRecommendedInfoBottomSheet,
  SwitchOnTrackFundInfoBottomSheet
} from '../../shared/notes/PlanImportantNoteStaticInfoBottomSheets'

type RebalancePlanImportantNoteProps = {
  importantNotes?: LumpsumRebalanceImportantNotes | null
}

export function RebalancePlanImportantNote({ importantNotes }: RebalancePlanImportantNoteProps) {
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()
  const notePoints = [
    ...buildRuleBasedImportantNotePoints(
      importantNotes?.ruleBased,
      openNestedBottomSheet,
      closeNestedBottomSheet
    ),
    ...buildFactBasedImportantNotePoints(
      importantNotes?.factBasedCuratedNotes,
      openNestedBottomSheet,
      closeNestedBottomSheet
    )
  ]

  if (notePoints.length === 0) {
    return null
  }

  return <PlanImportantNoteCard notePoints={notePoints} />
}

export function buildRuleBasedImportantNotePoints(
  staticNoteTypes: (LumpsumRebalanceImportantNoteStaticType | string)[] | null | undefined,
  openNestedBottomSheet: (content: ReactNode) => void,
  closeNestedBottomSheet: () => void
): ReactNode[] {
  const linkClassName =
    'w-fit cursor-pointer text-[13px] font-[400] leading-[140%] text-[#1470CC] underline underline-offset-2'
  const textClassName = 'text-[13px] font-[400] leading-[150%] text-[#3D3D3D]'
  const points: ReactNode[] = []

  for (const staticNoteType of staticNoteTypes ?? []) {
    if (staticNoteType === LumpsumRebalanceImportantNoteStaticType.BUY_FUND_NON_RANK_ONE) {
      points.push(
        <div>
          <div className={textClassName}>Rank 1 is not always recommended.</div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalanceRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <RankOneNotRecommendedInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            Learn why
          </div>
        </div>
      )
      continue
    }

    if (staticNoteType === LumpsumRebalanceImportantNoteStaticType.SWP_TRANSFER) {
      points.push(
        <div className={textClassName}>
          When moving large amounts, it&apos;s better to do it in instalments or SWP. This reduces
          the risk of wrong market timing.
        </div>
      )
      continue
    }

    if (staticNoteType === LumpsumRebalanceImportantNoteStaticType.HIGH_EXPOSURE_SWITCH) {
      points.push(
        <div className={textClassName}>
          This recommendation aims to reduce high exposure (concentration risk) in a single fund, keeping your portfolio
          well-diversified.
        </div>
      )
      continue
    }

    if (staticNoteType === LumpsumRebalanceImportantNoteStaticType.SELL_FUND_IDCW) {
      points.push(
        <div>
          <div className={textClassName}>We&apos;ve recommended a Growth Plan instead of IDCW.</div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalanceRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <GrowthPlanInsteadOfIdcwInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            Learn why
          </div>
        </div>
      )
      continue
    }

    if (staticNoteType === LumpsumRebalanceImportantNoteStaticType.SELL_FUND_ELSS) {
      points.push(
        <div>
          <div className={textClassName}>
            You can only switch the units that are out of the 3-year lock-in.
          </div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalanceRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <ElssRebalancingInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            How does this work?
          </div>
        </div>
      )
      continue
    }

    if (staticNoteType === LumpsumRebalanceImportantNoteStaticType.SELL_FUND_ON_TRACK) {
      points.push(
        <div>
          <div className={textClassName}>
            This recommendation aims to move your investment to an even better fund.
          </div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalanceRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <SwitchOnTrackFundInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            Why switch an On-track fund?
          </div>
        </div>
      )
    }
  }

  return points
}
